import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService, calendarService } from '../services/api';
import eulaService from '../services/eulaService';

const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
  eulaStatus: null,
  needsEulaAcceptance: false,
};

const AUTH_ACTIONS = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_EULA_STATUS: 'SET_EULA_STATUS',
  EULA_ACCEPTED: 'EULA_ACCEPTED',
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        loading: false,
        error: null,
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        eulaStatus: null,
        needsEulaAcceptance: false,
      };
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case AUTH_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    case AUTH_ACTIONS.SET_EULA_STATUS:
      return {
        ...state,
        eulaStatus: action.payload.eulaStatus,
        needsEulaAcceptance: action.payload.needsEulaAcceptance,
      };
    case AUTH_ACTIONS.EULA_ACCEPTED:
      return {
        ...state,
        needsEulaAcceptance: false,
        eulaStatus: {
          ...state.eulaStatus,
          user_has_accepted: true,
        },
      };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const response = await authService.getCurrentUser();
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user: response.data }
          });

          // Check EULA status after successful login
          try {
            const eulaStatus = await eulaService.getEulaStatus();
            dispatch({
              type: AUTH_ACTIONS.SET_EULA_STATUS,
              payload: {
                eulaStatus,
                needsEulaAcceptance: eulaStatus.has_active_eula && !eulaStatus.user_has_accepted
              }
            });
          } catch (eulaError) {
            // Don't block user if EULA check fails
          }
        } catch (error) {
          authService.clearTokens();
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      const response = await authService.login(credentials);
      const user = response.data; // Backend returns user directly, not wrapped

      // No need to set tokens manually - they're set as cookies by the backend
      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user }
      });

      // Check EULA status after login
      try {
        const eulaStatus = await eulaService.getEulaStatus();
        dispatch({
          type: AUTH_ACTIONS.SET_EULA_STATUS,
          payload: {
            eulaStatus,
            needsEulaAcceptance: eulaStatus.has_active_eula && !eulaStatus.user_has_accepted
          }
        });
      } catch (eulaError) {
        console.error('Failed to check EULA status:', eulaError);
      }

      // Fire-and-forget: sync calendar events so they're fresh on dashboard
      calendarService.googleCalendar.poll().catch(() => {});
      calendarService.outlookCalendar.poll().catch(() => {});

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      // Registration no longer returns a session — the address is unverified,
      // so the user must confirm it and then log in.
      const response = await authService.register(userData);

      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });

      return { success: true, email: response.data?.email || userData.email };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const googleLogin = async (token) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

    try {
      const response = await authService.googleLogin(token);
      const user = response.data; // Backend returns user directly

      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user }
      });

      // Fire-and-forget: sync calendar events so they're fresh on dashboard
      calendarService.googleCalendar.poll().catch(() => {});
      calendarService.outlookCalendar.poll().catch(() => {});

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Google login failed';
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      authService.clearTokens();
      localStorage.removeItem('user');
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Method to directly set user data (for password reset, etc.)
  const setUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    dispatch({
      type: AUTH_ACTIONS.LOGIN_SUCCESS,
      payload: { user: userData }
    });
  };

  // Method to mark EULA as accepted
  const acceptEula = () => {
    dispatch({ type: AUTH_ACTIONS.EULA_ACCEPTED });
  };

  // Method to check EULA status (can be called to refresh)
  const checkEulaStatus = async () => {
    try {
      const eulaStatus = await eulaService.getEulaStatus();
      dispatch({
        type: AUTH_ACTIONS.SET_EULA_STATUS,
        payload: {
          eulaStatus,
          needsEulaAcceptance: eulaStatus.has_active_eula && !eulaStatus.user_has_accepted
        }
      });
      return eulaStatus;
    } catch (error) {
      console.error('Failed to check EULA status:', error);
      throw error;
    }
  };

  const value = {
    ...state,
    login,
    register,
    googleLogin,
    logout,
    clearError,
    setUser,
    acceptEula,
    checkEulaStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
